import { useFieldArray, useForm } from '@/Hooks/useFormWrapper';
import { useRequired, useShouldBeEmail } from '@/Hooks/Validation';
import { emailSendModalAsyncActions } from '@/ModalWindows/EmailSendModal/services/reducer';
import { EmailModel } from '@/Models/EmailModel';
import { AppDispatch } from '@/Redux/ConfigureStore';
import { Nullable, SimpleObject } from '@/Types';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

export type FormData = Omit<EmailModel, 'attachments'> & {
  attachments: {
    name: string;
    body: string;
  }[];
};

export const useEmailPreviewModalForm = (data: Nullable<{ email: EmailModel; orderId: number }>) => {
  const dispatch: AppDispatch = useDispatch();

  // Methods
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const attachments = useFieldArray({
    name: 'attachments',
    control: control,
    keyName: 'array_id',
  });

  useEffect(() => {
    if (!data) return;

    const { email } = data;

    register('body');

    Object.keys(data.email.attachments).forEach((key, idx) => {
      register(`attachments.${idx}`);
    });

    reset({
      to: email.to,
      cc: email.cc,
      bcc: email.bcc,
      body: email.body,
      subject: email.subject,
      attachments: Object.keys(data.email.attachments).map((key) => ({
        name: key,
        body: data?.email.attachments[key] as string,
      })),
    });
  }, [data]);

  // Rules
  const required = useRequired();
  const valueMustBeEmail = useShouldBeEmail();

  // On form submit
  const onSubmit = useCallback(
    (formData: FormData) => {
      if (!data) return;

      dispatch(
        emailSendModalAsyncActions.sendFormattedEmailForOrder({
          orderId: data.orderId,
          email: {
            ...formData,
            attachments: formData.attachments.reduce((prev: SimpleObject, curr) => {
              prev[curr.name] = curr.body;
              return prev;
            }, {}),
          },
        }),
      );
    },
    [data],
  );

  return {
    methods: {
      register,
      errors,
    },
    arrays: {
      attachments,
    },
    rules: {
      required,
      valueMustBeEmail,
    },
    onSubmit: handleSubmit(onSubmit),
  };
};
